import { Box } from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import { FormControlLabel, IconButton } from "@mui/material";
import { MenuItem, Modal, Select } from "@mui/material";
import { Switch, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { APP_COLOR, TEXT_COLOR } from "../../theme";
import { shareModalStyles } from "./styles";
import CloseIcon from "@mui/icons-material/Close";
import { ShareFolderModalProps } from "./types";
import { NUMBER_REGEX } from "../constants";
import { toast } from "react-toastify";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { createAndCopyFolderShareUrl } from "../TrackComponents/TrackMenu/trackMenuHelper";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomButton from "../CustomButton/CustomButton";

export const ShareFolderModal: React.FC<ShareFolderModalProps> = ({
  id,
  title,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAllowDownload, setIsisAllowDownload] = useState<boolean>(true);
  const [maxViews, setMaxViews] = useState<any>(10);
  const [isShareLinkAvailable, setIsShareLinkAvailable] =
    useState<boolean>(false);
  const [isAllowUnlimitedViews, setIsAllowUnlimitedViews] =
    useState<boolean>(false);
  const [isValidMaxViews, setIsValidMaxViews] = useState<boolean>(true);
  const [selectValue, setSelectValue] = useState(1);
  const [shareLink, setShareLink] = useState<string>();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const handleOpen = () => setOpen(true);
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  const handleClose = () => {
    setLoading(false);
    setIsisAllowDownload(true);
    setMaxViews(10);
    setIsShareLinkAvailable(false);
    setIsAllowUnlimitedViews(false);
    setSelectValue(1);
    setOpen(false);
  };

  const handleCopyShareUrl = () => {
    if (shareLink) {
      navigator.clipboard
        .writeText(shareLink)
        .then(() => {
          console.log("URL successfully copied to clipboard");
          toast.info("URL copied");
        })
        .catch((err) => {
          console.error("Unable to copy URL to clipboard", err);
        });
    }
  };

  const calculateExpirationTimestamp = (hours: number): number => {
    const currentTime = new Date();
    let expirationTimestamp: number;

    switch (hours) {
      case 0: // 1 year
        expirationTimestamp = currentTime.getTime() + 365 * 24 * 60 * 60 * 1000;
        break;
      case 1: // 1 Hour
        expirationTimestamp = currentTime.getTime() + 1 * 60 * 60 * 1000;
        break;
      case 12: // 12 Hours
        expirationTimestamp = currentTime.getTime() + 12 * 60 * 60 * 1000;
        break;
      case 24: // 1 Day
        expirationTimestamp = currentTime.getTime() + 24 * 60 * 60 * 1000;
        break;
      case 168: // 1 Week
        expirationTimestamp = currentTime.getTime() + 7 * 24 * 60 * 60 * 1000;
        break;
      default: // Default to 1 Hour
        expirationTimestamp = currentTime.getTime() + 1 * 60 * 60 * 1000;
        break;
    }
    return expirationTimestamp;
  };

  const handleExpiryChange = (event: SelectChangeEvent<number>) => {
    setSelectValue(Number(event.target.value));
  };

  const handleGenerateLink = async () => {
    try {
      setLoading(true);
      const linkExpiryTime = calculateExpirationTimestamp(selectValue);
      if (id && NUMBER_REGEX.test(maxViews)) {
        const shareUrl = await createAndCopyFolderShareUrl(
          id,
          userId,
          await getValidAccessToken(),
          linkExpiryTime,
          maxViews,
          isAllowDownload,
          isAllowUnlimitedViews,
          title,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
        if (shareUrl) {
          setIsShareLinkAvailable(true);
          setShareLink(shareUrl);
          toast.success("Shareable link created and copied");
          try {
            await navigator.clipboard.writeText(shareUrl);
          } catch (clipboardError) {
            console.error("Unable to copy URL to clipboard", clipboardError);
          }
        } else {
          toast.error("Failed to generate shareable link");
        }
      } else {
        toast.warn("Please enter a valid number for max views");
      }
    } catch (error) {
      console.error("Error generating shareable link:", error);
      toast.error("Something went wrong while generating the link");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (maxViews < 1) {
      setIsValidMaxViews(false);
    } else {
      setIsValidMaxViews(true);
    }
    if (isAllowUnlimitedViews) {
      setIsValidMaxViews(true);
    }
  }, [isAllowUnlimitedViews, maxViews]);

  return (
    <>
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          handleOpen();
        }}
      >
        Share Folder
      </MenuItem>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={shareModalStyles.mainContainer}
        >
          <Box sx={shareModalStyles.topBarIconContainer}>
            <Box sx={shareModalStyles.shareIconContainer}>
              <img
                style={shareModalStyles.shareIcons}
                src="/assets/icons/TracksPageIcons/share-07.svg"
                alt="icon"
              />
            </Box>
            <Box sx={shareModalStyles.closeIconContainer}>
              <IconButton onClick={handleClose}>
                <CloseIcon sx={{ fontSize: "30px" }} />
              </IconButton>
            </Box>
          </Box>
          <Typography sx={shareModalStyles.title}>Share Folder</Typography>
          <FormControlLabel
            sx={{ color: TEXT_COLOR, marginTop: "15px" }}
            control={
              <Switch
                checked={isAllowDownload}
                onChange={() => setIsisAllowDownload(!isAllowDownload)}
              />
            }
            label="Allow Download"
          />
          <FormControlLabel
            sx={{ color: TEXT_COLOR }}
            control={
              <Switch
                checked={isAllowUnlimitedViews}
                onChange={() =>
                  setIsAllowUnlimitedViews(!isAllowUnlimitedViews)
                }
              />
            }
            label="Allow Unlimited Views"
          />
          {!isAllowUnlimitedViews && (
            <Box>
              <Typography sx={shareModalStyles.setMaxViews}>
                Set Max Views
              </Typography>
              <TextField
                error={!isValidMaxViews}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: APP_COLOR,
                      boxShadow:
                        "0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(158, 119, 237, 0.24)",
                    },
                  },
                }}
                disabled={isShareLinkAvailable || isAllowUnlimitedViews}
                type="number"
                value={maxViews}
                inputProps={{
                  pattern: "[0-9]*",
                }}
                onChange={(e) => {
                  const numberRegex = /^\d+$/;
                  if (numberRegex.test(e.target.value)) {
                    setMaxViews(Number(e.target.value));
                  } else {
                    setMaxViews(e.target.value);
                  }
                }}
              />
              {!isValidMaxViews && (
                <p style={{ color: "red", fontSize: "14px" }}>
                  Please enter valid number (greater than 0)
                </p>
              )}
            </Box>
          )}
          <Typography sx={shareModalStyles.typography}>
            Link Expiry Time
          </Typography>
          <Select
            MenuProps={{
              PaperProps: {
                sx: shareModalStyles.selectMenuProps,
              },
            }}
            sx={shareModalStyles.select}
            value={selectValue}
            onChange={(e) => handleExpiryChange(e)}
          >
            <MenuItem value={1}>1 Hour</MenuItem>
            <MenuItem value={12}>12 Hours</MenuItem>
            <MenuItem value={24}>1 Day</MenuItem>
            <MenuItem value={168}>1 Week</MenuItem>
            <MenuItem value={0}>1 Year</MenuItem>
          </Select>
          {shareLink && (
            <Box sx={{ marginTop: "20px", display: "flex" }}>
              <TextField
                fullWidth
                value={shareLink}
                disabled={isShareLinkAvailable}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleCopyShareUrl} edge="end">
                      <ContentCopyIcon sx={{ color: APP_COLOR }} />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          )}
          <Box sx={shareModalStyles.buttonsContainer}>
            <CustomButton
              text="Create Shareable Link"
              onClick={handleGenerateLink}
              isDisabled={!isValidMaxViews}
              loading={loading}
              customStyles={{ width: "100%" }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
